// =====================================================
// BED — Pré-remplissage du formulaire depuis un lien tokenisé (brief §3)
// =====================================================
// Quand le BED est ouvert via `?token=<bed_access_token>`, la RPC anon
// `get_bed_prefill` renvoie les données CRM déjà connues de l'équipage. Ce
// module : (1) type le retour de la RPC, (2) l'appelle, (3) mappe les valeurs
// sur les champs du formulaire (par contact_field_map + target_role) sous forme
// d'un dictionnaire { [fieldId]: value } prêt pour react-hook-form `setValue`.
// La fonction de mapping est PURE (testable en vitest, sans réseau ni React).

import { supabase } from '../supabase';
import type { FormFieldWithValues } from '../../types/form';

/** Données pré-remplissables d'un contact (pilote ou copilote). */
export interface BedPrefillContact {
  contact_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  birth_date?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  /** Valeur DB : 'homme' | 'femme' | 'autre' | null. */
  gender?: string | null;
  is_not_student?: boolean | null;
  socio_professional_category?: string | null;
  /** Nom de l'école (résolu via school_id). */
  school?: string | null;
}

/** Aperçu du solde BED (best-effort — null si prix produit non vérifié). */
export interface BedPrefillSolde {
  prix_total_cents: number;
  acompte_cents: number;
  solde_cents: number;
}

/** Retour complet de la RPC `get_bed_prefill`. */
export interface BedPrefill {
  crew: { id: string; name: string; crew_number: string | null } | null;
  product_id: string;
  parent_bpi_case_id: string;
  expires_at: string;
  pilot: BedPrefillContact | null;
  copilot: BedPrefillContact | null;
  solde: BedPrefillSolde | null;
}

/**
 * La colonne `contacts.gender` stocke des minuscules canoniques
 * (homme/femme/autre) alors que le champ select du BED expose des libellés
 * capitalisés (Homme/Femme/Autre) : on convertit pour que la valeur pré-remplie
 * corresponde bien à une option existante du select.
 */
const GENDER_DB_TO_OPTION: Record<string, string> = {
  homme: 'Homme',
  femme: 'Femme',
  autre: 'Autre',
};

/**
 * Construit le dictionnaire { [fieldId]: value } de pré-remplissage à partir des
 * champs du formulaire et des données CRM renvoyées par `get_bed_prefill`.
 *
 * Règles :
 *   - seuls les champs `contact_field_map` non nuls sont pré-remplis ;
 *   - la source dépend du `target_role` du champ (pilot → prefill.pilot,
 *     copilot → prefill.copilot ; les champs communs sans rôle sont ignorés car
 *     ils n'ont pas d'équivalent CRM par rôle) ;
 *   - les valeurs vides/absentes sont ignorées (le champ reste vide) ;
 *   - `gender` est converti en libellé d'option (Homme/Femme/Autre).
 *
 * PURE : aucune dépendance réseau/React — testable directement.
 */
export function buildBedPrefillValues(
  fields: FormFieldWithValues[],
  prefill: BedPrefill
): Record<string, string> {
  const out: Record<string, string> = {};

  for (const field of fields) {
    const map = field.contact_field_map;
    if (!map) continue;

    // Nom de l'équipage : champ commun (sans rôle) pré-rempli depuis crew.name.
    // Sentinel `crew_name` (ignoré par le trigger create_contact_from_submission,
    // ce n'est pas une colonne contact) : sert uniquement de point d'ancrage prefill.
    if (map === 'crew_name') {
      if (prefill.crew?.name) out[field.id] = prefill.crew.name;
      continue;
    }

    const contact =
      field.target_role === 'copilot'
        ? prefill.copilot
        : field.target_role === 'pilot'
          ? prefill.pilot
          : null;
    if (!contact) continue;

    const raw = (contact as Record<string, unknown>)[map];
    if (raw === null || raw === undefined || raw === '') continue;

    let value = String(raw);
    if (map === 'gender') {
      value = GENDER_DB_TO_OPTION[value.toLowerCase()] ?? value;
    }
    // `contacts.is_not_student` est un booléen ; les formulaires stockent le
    // libellé attendu par le trigger `create_contact_from_submission`
    // ('Jeune Actif' / 'Étudiant'). Sans cette conversion, un `"true"` brut
    // n'est reconnu ni par le renderer BPI ni par le wizard BED.
    if (map === 'is_not_student') {
      value = raw === true || value === 'true' ? 'Jeune Actif' : 'Étudiant';
    }
    out[field.id] = value;
  }

  return out;
}

/** Résultat de la résolution du token de pré-remplissage. */
export type GetBedPrefillResult =
  | { status: 'ok'; prefill: BedPrefill }
  | { status: 'invalid' }
  | { status: 'expired' }
  | { status: 'error' };

/**
 * Appelle la RPC anon `get_bed_prefill`. Ne throw jamais : renvoie un statut
 * discriminé pour que l'UI décide (token invalide/expiré → on ouvre un form
 * vierge, on ne bloque pas la saisie manuelle).
 */
export async function getBedPrefill(token: string): Promise<GetBedPrefillResult> {
  try {
    const { data, error } = await supabase.rpc('get_bed_prefill', { p_token: token });
    if (error) {
      if (error.message?.includes('_expired')) return { status: 'expired' };
      if (error.message?.includes('_invalid')) return { status: 'invalid' };
      console.error('[BED-PREFILL] Erreur get_bed_prefill:', error);
      return { status: 'error' };
    }
    if (!data) return { status: 'invalid' };
    return { status: 'ok', prefill: data as BedPrefill };
  } catch (err) {
    console.error('[BED-PREFILL] Exception get_bed_prefill:', err);
    return { status: 'error' };
  }
}
